import type { JSX } from 'react';
import { CloseOutlined, PlusOutlined, VideoCameraOutlined } from '@ant-design/icons';

import { videoLimitHint } from '../lib/room-media';
import { fmtDuration, fmtSize } from './opening-tasks/utils';
import type { RoomVideosState } from './useRoomVideos';

/** Список ошибок под полем (красным, переносится по словам). */
export function ErrorList({ errors }: { errors: string[] }): JSX.Element | null {
  if (!errors.length) return null;
  return (
    <ul role="alert" style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12.5, fontWeight: 600, color: 'var(--fk-error)', lineHeight: 1.4 }}>
      {errors.map((er, i) => (
        <li key={i} style={{ marginTop: i > 0 ? 4 : 0, wordBreak: 'break-word' }}>{er}</li>
      ))}
    </ul>
  );
}

/**
 * Блок «Видео помещения» в формах «Оценка помещения»: кнопка «Добавить видео»,
 * список выбранных роликов (имя, размер, длительность, крестик) и ошибки проверки.
 * Сами ролики уходят после создания заявки — сообщением в её переписку.
 */
export function RoomVideoField({
  state,
  disabled,
  title = 'Видео помещения',
  hint,
}: {
  state: RoomVideosState;
  /** Идёт отправка заявки — выбор и удаление закрыты. */
  disabled?: boolean;
  title?: string;
  hint?: string;
}): JSX.Element {
  const { videos, errors, preparing, limits } = state;
  const off = disabled || preparing;
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 8 }}>
        {title} {videos.length > 0 && <span style={{ color: 'var(--fk-text-muted)' }}>· {videos.length}</span>}
      </div>

      {videos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {videos.map((v) => (
            <div
              key={v.key}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-surface)' }}
            >
              <VideoCameraOutlined style={{ color: 'var(--fk-text-muted)', flex: 'none' }} />
              <span style={{ flex: '1 1 140px', minWidth: 0, fontSize: 13, color: 'var(--fk-text)', wordBreak: 'break-word' }}>{v.file.name}</span>
              <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', whiteSpace: 'nowrap' }}>
                {fmtSize(v.file.size)}
                {v.durationSec != null && v.durationSec > 0 ? ` · ${fmtDuration(v.durationSec)}` : ''}
              </span>
              <button
                type="button"
                onClick={() => state.remove(v.key)}
                disabled={disabled}
                aria-label={`Убрать ${v.file.name}`}
                title="Удалить"
                style={{ flex: 'none', width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'var(--fk-bg-subtle)', color: 'var(--fk-text-2)', cursor: disabled ? 'default' : 'pointer', display: 'grid', placeItems: 'center', fontSize: 11, fontFamily: 'inherit' }}
              >
                <CloseOutlined />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 'var(--fk-radius-md)', border: '1.5px dashed var(--fk-border-strong)', color: off ? 'var(--fk-text-muted)' : 'var(--fk-text-2)', fontSize: 13, fontWeight: 600, cursor: off ? 'default' : 'pointer', fontFamily: 'inherit' }}
        >
          <PlusOutlined /> Добавить видео
          <input
            type="file"
            accept="video/*"
            multiple
            disabled={off}
            style={{ display: 'none' }}
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              e.target.value = '';
              void state.add(picked);
            }}
          />
        </label>
        {preparing && <span style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>Проверяем видео…</span>}
      </div>

      <ErrorList errors={errors} />

      <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginTop: 8, lineHeight: 1.45 }}>
        {videoLimitHint(limits)}.{' '}
        {hint ?? 'Видео уйдёт куратору в переписку по заявке («Мои задачи» → «Мои помещения»).'}
      </div>
    </div>
  );
}
